<template>
    <div class="fixed top-0 right-0 h-screen bg-white z-50 overflow-y-scroll transition duration-300 ease-in-out" :class="parent.id === activeTrigger ? 'w-full' : 'translate-x-full w-0'">
        <div class="flex gap-4 justify-between items-center bg-base-100 pl-1 pr-1 py-[2px]">
            <button
                tabindex="0"
                type="button"
                class="btn btn-ghost btn-sm btn-icon-left mr-auto"
                @click="closeCategories"
            >
                <MiscIcon name="chevron-left" />
                {{ $t('layout.navigation.mobile.subcategory.back') }}
            </button>

            <button
                type="button"
                tabindex="0"
                class="btn btn-ghost btn-circle ml-auto"
                @click="closeDrawer"
            >
                <MiscIcon name="x" />
            </button>
        </div>

        <div class="bg-base-100 flex items-center gap-6 pl-4 pr-2 justify-between h-12">
            <span class="text-lg text-neutral font-semibold">{{ getTranslatedProperty(parent, 'name') }}</span>

            <MiscLink no-prefetch :to="getCategoryRoute(parent)" class="btn btn-xs btn-ghost btn-icon-right">
                {{ $t('layout.navigation.mobile.subcategory.viewAll') }}
                <MiscIcon name="chevron-right" size="sm" />
            </MiscLink>
        </div>

        <template v-if="children != null && children?.length > 0">
            <ul class="flex flex-col border-b">
                <template v-for="(group, groupType) in groupCategoriesBySection(children)" :key="groupType">
                    <template v-if="group.children.length > 0 && groupType !== 'is_top_brand'">
                        <li class="pl-4 pr-2 mt-2 h-12 text-sm font-semibold border-b flex items-center">
                            {{ group.name }}
                        </li>

                        <li v-for="childItem in group.children" :key="childItem.id">
                            <template v-if="getFlyoutChildren(childItem).length > 0">
                                <button
                                    tabindex="0"
                                    type="button"
                                    class="pl-4 pr-2 h-12 text-sm font-semibold text-neutral bg-base-100 flex justify-between items-center gap-3 w-full sticky top-0 text-left"
                                    @click="toggleCategories(childItem.id)"
                                >
                                    {{ getTranslatedProperty(childItem, 'name') }}

                                    <span class="btn btn-xs btn-ghost btn-icon-right">
                                        {{ $t('layout.navigation.mobile.subcategory.viewAll') }}
                                        <MiscIcon name="chevron-right" size="sm" />
                                    </span>
                                </button>

                                <LayoutNavigationMobileSubcategory
                                    v-if="subTrigger === childItem.id"
                                    :parent="childItem"
                                    :children="childItem.children"
                                    :active-trigger="subTrigger"
                                    @on-close="subTrigger = false"
                                />
                            </template>
                            <MiscLink
                                v-else
                                no-prefetch
                                :to="getCategoryRoute(childItem)"
                                class="pl-4 pr-4 h-12 text-sm text-neutral bg-base-100 flex justify-between items-center gap-3 w-full sticky top-0 text-left"
                            >
                                {{ getTranslatedProperty(childItem, 'name') }}
                                <MiscIcon name="chevron-right" size="sm" />
                            </MiscLink>
                        </li>
                    </template>

                    <div v-if="group.children.length > 0 && groupType === 'is_top_brand'" class="flex flex-col gap-6 pt-6 pb-9 border-t">
                        <div class="flex flex-col items-center gap-2 p-3">
                            <div class="text-xs text-base-content-subtle">
                                {{ $t('layout.navigation.mobile.topBrandTitle') }}
                            </div>
                            <div class="text-sm text-secondary-content font-bold">
                                {{ $t('layout.navigation.mobile.topBrandSubTitle') }}
                            </div>
                        </div>
                        <div class="grid grid-cols-2 gap-4 px-4">
                            <MiscLink
                                v-for="childItem in group.children"
                                :key="childItem.id"
                                :to="getCategoryRoute(childItem)"
                                active-class="none"
                                class="col-span-1 bg-base-200 no-underline py-1 px-2 flex justify-center items-center"
                                no-prefetch
                            >
                                <MiscImage
                                    v-if="childItem.extensions?.a4g_brand != null && childItem.extensions?.a4g_brand?.media != null"
                                    :src="getMediaUrl(childItem.extensions?.a4g_brand?.media)"
                                    :alt="childItem.extensions?.a4g_brand?.media.translated?.alt"
                                    :media-width="childItem.extensions?.a4g_brand?.media.metaData?.width"
                                    :media-height="childItem.extensions?.a4g_brand?.media.metaData?.height"
                                    class=" min-h-[60px]"
                                    lazy-load
                                />
                                <strong v-else class="text-primary text-sm font-semibold">{{ getTranslatedProperty(childItem, 'name') }}</strong>
                            </MiscLink>
                        </div>
                    </div>
                </template>
            </ul>
            <div v-if="cmsContent" class="pb-2">
                <StructurePage :content="cmsContent" />
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import { getCategoryBreadcrumbs, getCategoryRoute, getTranslatedProperty } from '@shopware/helpers'

const props = defineProps<{
    parent: Schemas['Category'],
    children: Schemas['Category'][],
    activeTrigger: boolean | string,
    cmsContent?: Schemas['CmsPage'] | null
}>()

const subTrigger = ref<boolean | string>(false)

// Open active category via breadcrumb
const { breadcrumbs } = useBreadcrumbs()
// Exact match: expand the specific sub-section the current page belongs to.
// startIndex: 1 skips "Home" — compare by name only, same reason as parent component.
if ((breadcrumbs.value?.length ?? 0) > 0) {
    const currentNames = breadcrumbs.value!.map(b => b.name)
    props.children?.forEach((category) => {
        const catNames = getCategoryBreadcrumbs(category, { startIndex: 1 }).map(b => b.name)
        if (JSON.stringify(catNames) === JSON.stringify(currentNames)) {
            subTrigger.value = category.id
        }
    })
}

const { t } = useI18n()
const { close } = useSidenav()
const closeDrawer = () => close('menu')
const emit = defineEmits(['onClose'])

function toggleCategories(trigger: string | boolean) {
    subTrigger.value = trigger;
}

function closeCategories() {
    emit('onClose')
}

function getMediaUrl(media: Schemas['Media']) {
    if (!media) {
        return '/'
    }

    return media.path
}

function groupCategoriesBySection (categories: Schemas['Category'][]) {
    const result: Record<string, { name: string; children: Schemas['Category'][] }> = {
        categories: {
            name: t('layout.navigation.mobile.categoriesTitle'),
            children: [],
        },
        is_highlight: {
            name: t('layout.navigation.mobile.highlightsTitle'),
            children: [],
        },
        is_new_sale: {
            name: t('layout.navigation.mobile.newSaleTitle'),
            children: [],
        },
        is_top_brand: {
            name: t('layout.navigation.mobile.topBrandTitle'),
            children: [],
        },
    }

    // Mobile navigation follows the flyout: hidden-in-flyout and non-top brand categories are skipped
    categories.filter(isCategoryShownInFlyout).forEach((item) => {
        const section = item?.customFields?.custom_category_flyout_section || 'categories'
        result[section]?.children?.push(item)
    })

    return result
}
</script>
